import type { WasteTypeConfig } from "@/pages/configuration/useConfigService";
import {
  REPORTING_TABLE_UNIT,
  REPORTING_VIEW_TYPES,
} from "@/pages/reporting_v2/types";

import type { UnitConverter } from "../../types";

const toCo2Factors = (
  configs: { wasteTypeCode: string; co2factor?: number | null }[],
) =>
  Object.fromEntries(
    configs.flatMap((config) =>
      config.co2factor
        ? [[config.wasteTypeCode, Number(config.co2factor)]]
        : [],
    ),
  );

export const getUnitConverter = (
  unit: REPORTING_TABLE_UNIT,
  co2FactorPerUnit: Record<string, number>,
  costPerUnit: Record<string, number>,
): UnitConverter => {
  switch (unit) {
    case REPORTING_TABLE_UNIT.EMISSIONS:
      return (code, kg) => (co2FactorPerUnit[code] ?? 0) * kg;
    case REPORTING_TABLE_UNIT.COST:
      return (code, kg) => (costPerUnit[code] ?? 0) * kg;
    default:
      return (_code, kg) => kg;
  }
};

export const getReportingConversionFunction = ({
  viewType,
  wasteTypeClientConfig,
  terminalWasteTypeConfig,
  unit,
}: {
  viewType: REPORTING_VIEW_TYPES;
  wasteTypeClientConfig: WasteTypeConfig[];
  terminalWasteTypeConfig: WasteTypeConfig[];
  unit: REPORTING_TABLE_UNIT;
}) => {
  const isPortfolioView = viewType === REPORTING_VIEW_TYPES.PORTFOLIO;

  const co2FactorPerUnit = isPortfolioView
    ? toCo2Factors(wasteTypeClientConfig)
    : {
        ...toCo2Factors(wasteTypeClientConfig),
        ...toCo2Factors(terminalWasteTypeConfig),
      };

  // Costs are configured per building, so they only apply in building view.
  const costPerUnit = isPortfolioView
    ? {}
    : Object.fromEntries(
        terminalWasteTypeConfig.flatMap((config) =>
          config.currentCost
            ? [[config.wasteTypeCode, config.currentCost.costPerUnit]]
            : [],
        ),
      );
  return getUnitConverter(unit, co2FactorPerUnit, costPerUnit);
};
